/* eslint-disable unicorn/prefer-spread */
import { Error, ScheduleSend, Send } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { COLORS } from 'constants/colors'

/**
 * Returns the color corresponding to the isActive status.
 *
 * @param status The isActive status.
 * @returns The color string.
 */
export const getIsActiveColor = (status: boolean | null): string => {
  if (status === null) {
    return 'gray'
  }
  return status ? COLORS.Success : COLORS.Error
}

/**
 * Formats a date to YYYY-MM-DD string format.
 *
 * @param date The input date.
 * @returns The formatted date string.
 */
export const dateFormatter = (date: Date) =>
  `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(
    -2,
  )}-${date.getDate()}`

/**
 * Returns the corresponding icon based on the status.
 *
 * @param status The status string.
 * @returns The icon component.
 */
export const getIcons = (status: string) => {
  if (status === 'SCHEDULED')
    return <ScheduleSend sx={{ marginRight: '5px' }} />
  if (status === 'ERROR_DURING_SEND')
    return <Error sx={{ marginRight: '5px' }} />
  return <Send sx={{ marginRight: '5px' }} />
}

/**
 * Returns the color for account row based on a boolean value.
 *
 * @param is The boolean value indicating whether the row is colored.
 * @returns The color string.
 */
export const coloredAccountRow = (is: boolean) =>
  is ? 'inherit' : COLORS.Error

/**
 * Returns the color for data row based on a boolean value.
 *
 * @param is The boolean value indicating whether the row is colored.
 * @returns The color string.
 */
export const coloredDataRow = (is: boolean) => (is ? '#0070c0' : '#EE9D00')

/**
 * Returns the color corresponding to the status.
 *
 * @param status The status string.
 * @returns The color string.
 */
export const getStatusColor = (status: string) => {
  if (status === 'SCHEDULED') return COLORS.Scheduled
  if (status === 'ERROR_DURING_SEND') return COLORS.Error
  return COLORS.Success
}
/**
 * Returns the name corresponding to the status.
 *
 * @param status The status string.
 * @returns The name string.
 */
export const getStatusName = (status: string) => {
  if (status === 'SENT_BY_SCHEDULER') return 'SCHEDULER'
  if (status === 'SENT_BY_API_REQUEST') return 'REQUEST'
  if (status === 'ERROR_DURING_SEND') return 'ERROR'
  return status
}

/**
 * Copies the specified text to clipboard.
 *
 * @param textToCopy The text to copy.
 */
export const copyToClipboard = async (textToCopy: string) => {
  // Navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy)
  } else {
    // Use the 'out of viewport hidden text area' trick
    const textArea = document.createElement('textarea')
    textArea.value = textToCopy

    // Move textarea out of the viewport so it's not visible
    textArea.style.position = 'absolute'
    textArea.style.left = '-999999px'

    document.body.prepend(textArea)
    textArea.select()

    try {
      document.execCommand('copy')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      textArea.remove()
    }
  }
}

/**
 * Replaces 'T' in the date string with a space and returns a Typography component.
 *
 * @param dateString The input date string.
 * @returns A Typography component.
 */
export const replaceTWithSpace = (dateString: string) => (
  <Typography whiteSpace={'pre-line'}>
    {dateString.replace('T', '\n')}
  </Typography>
)

/**
 * Returns the browser name based on the user agent string.
 *
 * @param userAgent The user agent string.
 * @returns The browser name string.
 */
export const getBrowserName = (userAgent: string) => {
  let browserName
  if (userAgent.includes('Firefox')) {
    browserName = 'Firefox'
  } else if (userAgent.includes('Edg')) {
    browserName = 'Edge'
  } else if (userAgent.includes('Chrome')) {
    browserName = 'Chrome'
  } else if (userAgent.includes('Safari')) {
    browserName = 'Safari'
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    browserName = 'Internet Explorer'
  } else {
    browserName = 'Unknown'
  }
  return browserName
}

/**
 * Returns the browser version based on the user agent string.
 *
 * @param userAgent The user agent string.
 * @returns The browser version string.
 */
export const getBrowserVersion = (userAgent: string) => {
  let version
  if (userAgent.includes('Firefox')) {
    version = userAgent.match(/Firefox\/([\d.]+)/)![1]
  } else if (userAgent.includes('Edg')) {
    version = userAgent.match(/Edg\/([\d.]+)/)![1]
  } else if (userAgent.includes('Chrome')) {
    version = userAgent.match(/Chrome\/([\d.]+)/)![1]
  } else if (userAgent.includes('Safari')) {
    version = userAgent.match(/Version\/([\d.]+)/)![1]
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    // eslint-disable-next-line security/detect-unsafe-regex
    version = userAgent.match(/(?:MSIE |rv:)(\d+(\.\d+)?)/)![1]
  } else {
    version = 'Unknown'
  }
  return version
}

export const getCssObject = (value: string) => {
  return value
    .split(';')
    .filter(item => item.trim())
    .reduce((acc: any, item) => {
      const [key, val] = item.split(':').map(str => str.trim())
      if (key && val) {
        acc[key] = val
      }
      return acc
    }, {})
}

export const extractContentAndStyles = (html: string) => {
  const container = document.createElement('div')
  container.innerHTML = html
  const paragraphs = Array.from(container.querySelectorAll('p'))

  const css = paragraphs.map((p, index) => ({
    paragraph: `p-${index + 1}`,
    style: p.getAttribute('style') || '',
  }))

  const contentArray = paragraphs.map(p => {
    // Check if the paragraph contains an anchor tag or an img tag
    const anchor = p.querySelector('a')
    const img = p.querySelector('img')
    return anchor || img ? p.innerHTML : p.textContent
  })

  const content = contentArray.join('\n')

  return { content, css }
}
